import { Box } from '@components/ui/atoms/Box';
import { Button } from '@components/ui/atoms/Button';
import { Flex } from '@components/ui/atoms/Flex';
import { Heading } from '@components/ui/atoms/Heading';
import { Input } from '@components/ui/atoms/Input';
import { Stack } from '@components/ui/atoms/Stack';
import { Text } from '@components/ui/atoms/Text';
import { Textarea } from '@components/ui/atoms/Textarea';
import React, { useState } from 'react';

type Props = {
  compact?: boolean;
};

export const ContactSupportForm: React.FC<Props> = ({ compact = false }) => {
  const [result, setResult] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (data: FormData) => {
    const newErrors: Record<string, string> = {};
    const name = data.get('name') as string;
    const email = data.get('email') as string;
    const subject = data.get('subject') as string;
    const message = data.get('message') as string;

    if (!name.trim()) newErrors.name = 'Name is required';

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!subject.trim()) newErrors.subject = 'Subject is required';

    if (!message.trim()) newErrors.message = 'Message is required';

    return newErrors;
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrors({});
    setResult('');

    const formData = new FormData(event.currentTarget);
    const validationErrors = validateForm(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);

      return;
    }

    setResult('Sending....');
    formData.append('access_key', '48876760-b3db-4d01-aa96-e3424d0a6957'.trim());

    const response = await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      body: formData,
    });
    const data = await response.json();

    if (data.success) {
      setResult('Submitted!');
      event.currentTarget.reset();
    } else {
      setResult('Failed to Submit!');
    }
  };

  const handleChange = (event: React.FormEvent<HTMLFormElement>) => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const { name, value } = target;

    if (!name) return;

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };

      switch (name) {
        case 'name':
        case 'subject':
        case 'message':
          if (!value.trim()) {
            newErrors[name] = `${name.charAt(0).toUpperCase() + name.slice(1)} is required`;
          } else {
            delete newErrors[name];
          }

          break;
        case 'email':
          if (!value.trim()) {
            newErrors.email = 'Email is required';
          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            newErrors.email = 'Invalid email format';
          } else {
            delete newErrors.email;
          }

          break;
        default:
          break;
      }

      return newErrors;
    });

    if (result.includes('Failed')) {
      setResult('');
    }
  };

  const ErrorMessage = ({ message }: { message?: string }) => {
    if (!message) return null;

    return (
      <Text color="red.500" fontSize="11px" fontWeight="600" mt="4px" ml="2px">
        {message}
      </Text>
    );
  };

  return (
    <Box
      bg="white"
      borderRadius="xl"
      boxShadow="0 25px 60px -15px rgba(0,0,0,0.05), 0 0 20px rgba(0,0,0,0.02)"
      p={{ base: '2rem', md: '3.5rem' }}
      mt={compact ? { base: '2rem', md: '3rem' } : { base: '10rem', md: '3rem' }}
      position="relative"
    >
      <Heading
        fontSize="18px"
        fontWeight="800"
        color="#2D3748"
        mb="0.75rem"
        letterSpacing="-0.01em"
      >
        Questions? Drop us a Message
      </Heading>
      <Text fontSize="13px" color="#556987" lineHeight="1.6" mb="2.5rem">
        Feel free to reach out to us through the contact form below. Our team will respond promptly
        to assist you.
      </Text>

      <form onSubmit={(e) => void onSubmit(e)} onChange={handleChange} noValidate>
        <Stack gap="1.5rem">
          <Flex
            gap={{ base: '1.5rem', md: '1rem' }}
            direction={{ base: 'column', sm: 'row' }}
            align="flex-start"
          >
            <Box w="full">
              <Input
                name="name"
                placeholder="Your Name"
                w="full"
                h="46px"
                px="1rem"
                border="1px solid"
                borderColor={errors.name ? 'red.400' : '#E2E8F0'}
                borderRadius="md"
                fontSize="13px"
                color="gray.800"
                bg="transparent"
                _placeholder={{ fontStyle: 'italic', color: 'gray.400' }}
                _focus={{
                  outline: 'none',
                  borderColor: errors.name ? 'red.400' : '#735BF2',
                  boxShadow: `0 0 0 1px ${errors.name ? '#FC8181' : '#735BF2'}`,
                }}
              />
              <ErrorMessage message={errors.name} />
            </Box>

            <Box w="full">
              <Input
                name="email"
                placeholder="Your Email"
                w="full"
                h="46px"
                px="1rem"
                border="1px solid"
                borderColor={errors.email ? 'red.400' : '#E2E8F0'}
                borderRadius="md"
                fontSize="13px"
                color="gray.800"
                bg="transparent"
                _placeholder={{ fontStyle: 'italic', color: 'gray.400' }}
                _focus={{
                  outline: 'none',
                  borderColor: errors.email ? 'red.400' : '#735BF2',
                  boxShadow: `0 0 0 1px ${errors.email ? '#FC8181' : '#735BF2'}`,
                }}
              />
              <ErrorMessage message={errors.email} />
            </Box>
          </Flex>

          <Box w="full">
            <Input
              name="subject"
              placeholder="Subject"
              w="full"
              h="46px"
              px="1rem"
              border="1px solid"
              borderColor={errors.subject ? 'red.400' : '#E2E8F0'}
              borderRadius="md"
              fontSize="13px"
              color="gray.800"
              bg="transparent"
              _placeholder={{ fontStyle: 'italic', color: 'gray.400' }}
              _focus={{
                outline: 'none',
                borderColor: errors.subject ? 'red.400' : '#735BF2',
                boxShadow: `0 0 0 1px ${errors.subject ? '#FC8181' : '#735BF2'}`,
              }}
            />
            <ErrorMessage message={errors.subject} />
          </Box>

          <Box w="full">
            <Textarea
              name="message"
              placeholder="Message..."
              w="full"
              minH="120px"
              py="1rem"
              px="1rem"
              border="1px solid"
              borderColor={errors.message ? 'red.400' : '#E2E8F0'}
              borderRadius="md"
              fontSize="13px"
              color="gray.800"
              bg="transparent"
              resize="none"
              _placeholder={{ fontStyle: 'italic', color: 'gray.400' }}
              _focus={{
                outline: 'none',
                borderColor: errors.message ? 'red.400' : '#735BF2',
                boxShadow: `0 0 0 1px ${errors.message ? '#FC8181' : '#735BF2'}`,
              }}
            />
            <ErrorMessage message={errors.message} />
          </Box>

          <Box mt="0.5rem" display="flex" alignItems="center" gap="1rem">
            <Button visual="primary" size="lg" px="2rem" borderRadius="md" type="submit">
              Submit
            </Button>
            <Text
              fontStyle="italic"
              fontSize="14px"
              color={result.includes('Failed') ? 'red.500' : 'gray.500'}
              fontWeight="600"
            >
              {result}
            </Text>
          </Box>
        </Stack>
      </form>
    </Box>
  );
};

ContactSupportForm.displayName = 'ContactSupportForm';
